import { MakeLogicType, actions, afterMount, connect, kea, listeners, path, reducers, selectors } from 'kea'
import { loaders } from 'kea-loaders'
import posthog from 'posthog-js'

import api, { ApiConfig } from 'lib/api'
import { SetupTaskId, globalSetupLogic } from 'lib/components/ProductSetup'
import { OrganizationMembershipLevel } from 'lib/constants'
import { IconSwapHoriz } from 'lib/lemon-ui/icons'
import { lemonToast } from 'lib/lemon-ui/LemonToast/LemonToast'
import { featureFlagLogic } from 'lib/logic/featureFlagLogic'
import { DEFAULT_CURRENCY } from 'lib/utils/currency'
import { eventUsageLogic } from 'lib/utils/eventUsageLogic'
import { isUserLoggedIn } from 'lib/utils/getAppContext'
import { getAppContext } from 'lib/utils/getAppContext'
import {
    type ProductCrossSellProperties,
    type ProductIntentProperties,
    addProductIntent,
    addProductIntentForCrossSell,
} from 'lib/utils/product-intents'
import { identifierToHuman } from 'lib/utils/strings'

import { customProductsLogic } from '~/layout/panel-layout/ProjectTree/customProductsLogic'
import {
    CurrencyCode,
    CustomerAnalyticsConfig,
    type ProductIntentContext,
    ProductKey,
} from '~/queries/schema/schema-general'
import { CorrelationConfigType, ProjectType, TeamPublicType, TeamType } from '~/types'

import type { FeatureFlagsSet } from '../lib/logic/featureFlagLogic'
import { organizationLogic } from './organizationLogic'
import { projectLogic } from './projectLogic'
import { userLogic } from './userLogic'

const parseUpdatedAttributeName = (attr: keyof TeamType | null): string => {
    if (attr === 'app_urls') {
        return 'Authorized URLs'
    }

    if (attr === 'web_analytics_pre_aggregated_tables_enabled') {
        return 'New query engine'
    }

    if (attr === 'session_recording_minimum_duration_milliseconds') {
        return 'Session recording minimum duration'
    }

    return attr ? identifierToHuman(attr) : 'Project'
}

/** Return whether the provided value is a full TeamType object that's only available when authenticated. */
export function isAuthenticatedTeam(team: TeamType | TeamPublicType | undefined | null): team is TeamType {
    return !!team && 'api_token' in team
}

/**
 * Product-intent endpoints echo a full team serialized from a snapshot taken when their request
 * started. Replacing `currentTeam` wholesale with that snapshot can clobber fields committed by a
 * concurrent team PATCH — e.g. the onboarding-completion PATCH racing `complete_product_onboarding`
 * loses `has_completed_onboarding_for`, so `hasOnboardedAnyProduct` flips back to false and
 * sceneLogic bounces the user into /onboarding on every navigation. Keep the local team and take
 * only `product_intents`, the one field these endpoints actually change.
 */
function withProductIntentsFrom(
    currentTeam: TeamType | TeamPublicType | null,
    response: TeamType | null
): TeamType | TeamPublicType | null {
    if (!currentTeam || !response) {
        return response ?? currentTeam
    }
    if (response.id !== currentTeam.id) {
        // The response is for a different team than the one now active (the team switched while
        // the request was in flight) — don't graft the old team's intents onto the current one.
        return currentTeam
    }
    return { ...currentTeam, product_intents: response.product_intents }
}

export interface FrequentMistakeAdvice {
    key: string
    type: 'event' | 'person'
    fix: string
}

export interface ProductOnboardingCompleteProperties {
    product_type: ProductKey
    intent_context?: ProductIntentContext
}

export async function completeProductOnboarding(
    teamId: TeamType['id'] | string,
    { product_type, intent_context }: ProductOnboardingCompleteProperties
): Promise<TeamType | null> {
    return await api.update(`api/environments/${teamId}/complete_product_onboarding`, {
        product_type,
        intent_context,
    })
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface teamLogicValues {
    featureFlags: FeatureFlagsSet // featureFlagLogic
    currentProject: ProjectType | null // projectLogic
    baseCurrency: CurrencyCode
    currentProjectId: number | string
    currentTeam: TeamPublicType | TeamType | null
    currentTeamId: number | null
    currentTeamIdStrict: number | string
    currentTeamLoading: boolean
    customerAnalyticsConfig: CustomerAnalyticsConfig
    demoOnlyProject: boolean
    funnelCorrelationConfig: CorrelationConfigType
    hasOnboardedAnyProduct: boolean
    isCurrentTeamUnavailable: boolean
    isTeamTokenResetAvailable: boolean
    teamBeingDeleted: TeamType | null
    testAccountFilterFrequentMistakes: FrequentMistakeAdvice[]
    timezone: string
    weekStartDay: number
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface teamLogicActions {
    loadCustomProducts: () => any // customProductsLogic
    loadCurrentOrganization: () => any // organizationLogic
    loadCurrentProjectSuccess: (
        currentProject: ProjectType | null,
        payload?: any
    ) => {
        currentProject: ProjectType | null
        payload?: any
    } // projectLogic
    loadUser: (resetOnFailure?: boolean | undefined) => {
        resetOnFailure: boolean | undefined
    } // userLogic
    switchTeam: (
        teamId: number | string,
        destination?: string | undefined
    ) => {
        destination: string | undefined
        teamId: number | string
    } // userLogic
    addProductIntent: (properties: ProductIntentProperties) => ProductIntentProperties
    addProductIntentFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    addProductIntentForCrossSell: (properties: ProductCrossSellProperties) => ProductCrossSellProperties
    addProductIntentForCrossSellFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    addProductIntentForCrossSellSuccess: (
        currentTeam: TeamPublicType | TeamType | null,
        payload?: ProductCrossSellProperties
    ) => {
        currentTeam: TeamPublicType | TeamType | null
        payload?: ProductCrossSellProperties
    }
    addProductIntentSuccess: (
        currentTeam: TeamPublicType | TeamType | null,
        payload?: ProductIntentProperties
    ) => {
        currentTeam: TeamPublicType | TeamType | null
        payload?: ProductIntentProperties
    }
    createTeam: ({ name, is_demo }: { is_demo: boolean; name: string }) => {
        name: string
        is_demo: boolean
    }
    createTeamFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    createTeamSuccess: (
        currentTeam: TeamPublicType | TeamType | null,
        payload?: {
            name: string
            is_demo: boolean
        }
    ) => {
        currentTeam: TeamPublicType | TeamType | null
        payload?: {
            name: string
            is_demo: boolean
        }
    }
    deleteSecretTokenBackup: () => any
    deleteSecretTokenBackupFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    deleteSecretTokenBackupSuccess: (
        currentTeam: TeamPublicType | TeamType | null,
        payload?: any
    ) => {
        currentTeam: TeamPublicType | TeamType | null
        payload?: any
    }
    deleteTeam: (team: TeamType) => {
        team: TeamType
    }
    deleteTeamFailure: () => {
        value: true
    }
    deleteTeamSuccess: () => {
        value: true
    }
    loadCurrentTeam: () => any
    loadCurrentTeamFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    loadCurrentTeamSuccess: (
        currentTeam: TeamPublicType | null,
        payload?: any
    ) => {
        currentTeam: TeamPublicType | null
        payload?: any
    }
    recordProductIntentOnboardingComplete: (
        properties: ProductOnboardingCompleteProperties
    ) => ProductOnboardingCompleteProperties
    recordProductIntentOnboardingCompleteFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    recordProductIntentOnboardingCompleteSuccess: (
        currentTeam: TeamPublicType | TeamType | null,
        payload?: ProductOnboardingCompleteProperties
    ) => {
        currentTeam: TeamPublicType | TeamType | null
        payload?: ProductOnboardingCompleteProperties
    }
    resetToken: () => any
    resetTokenFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    resetTokenSuccess: (
        currentTeam: TeamPublicType | TeamType | null,
        payload?: any
    ) => {
        currentTeam: TeamPublicType | TeamType | null
        payload?: any
    }
    rotateSecretToken: () => any
    rotateSecretTokenFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    rotateSecretTokenSuccess: (
        currentTeam: TeamPublicType | TeamType | null,
        payload?: any
    ) => {
        currentTeam: TeamPublicType | TeamType | null
        payload?: any
    }
    updateCurrentTeam: (payload: Partial<TeamType>) => Partial<TeamType>
    updateCurrentTeamFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    updateCurrentTeamSuccess: (
        currentTeam: TeamPublicType | TeamType,
        payload?: Partial<TeamType>
    ) => {
        currentTeam: TeamPublicType | TeamType
        payload?: Partial<TeamType>
    }
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface teamLogicMeta {
    __keaTypeGenInternalSelectorTypes: {
        hasOnboardedAnyProduct: (currentTeam: TeamPublicType | TeamType | null) => boolean
        currentTeamId: (currentTeam: TeamPublicType | TeamType | null) => number | null
        currentTeamIdStrict: (currentTeam: TeamPublicType | TeamType | null) => number | string
        currentProjectId: (currentTeam: TeamPublicType | TeamType | null) => number | string
        isCurrentTeamUnavailable: (
            currentTeam: TeamPublicType | TeamType | null,
            currentTeamLoading: boolean
        ) => boolean
        demoOnlyProject: (
            currentTeam: TeamPublicType | TeamType | null,
            currentOrganization: null | import('~/types').OrganizationType
        ) => boolean
        funnelCorrelationConfig: (currentTeam: TeamPublicType | TeamType | null) => CorrelationConfigType
        timezone: (currentTeam: TeamPublicType | TeamType | null) => string
        weekStartDay: (currentTeam: TeamPublicType | TeamType | null) => number
        isTeamTokenResetAvailable: (currentTeam: TeamPublicType | TeamType | null) => boolean
        testAccountFilterFrequentMistakes: (currentTeam: TeamPublicType | TeamType | null) => FrequentMistakeAdvice[]
        baseCurrency: (currentTeam: TeamPublicType | TeamType | null) => CurrencyCode
        customerAnalyticsConfig: (currentTeam: TeamPublicType | TeamType | null) => CustomerAnalyticsConfig
    }
}

export type teamLogicType = MakeLogicType<teamLogicValues, teamLogicActions, Record<string, any>, teamLogicMeta>

export const teamLogic = kea<teamLogicType>([
    path(['scenes', 'teamLogic']),
    connect(() => ({
        values: [projectLogic, ['currentProject'], featureFlagLogic, ['featureFlags']],
        actions: [
            userLogic,
            ['loadUser', 'switchTeam'],
            organizationLogic,
            ['loadCurrentOrganization'],
            customProductsLogic,
            ['loadCustomProducts'],
            projectLogic,
            ['loadCurrentProjectSuccess'],
        ],
    })),
    actions({
        deleteTeam: (team: TeamType) => ({ team }),
        deleteTeamSuccess: true,
        deleteTeamFailure: true,
    }),
    reducers({
        teamBeingDeleted: [
            null as TeamType | null,
            {
                deleteTeam: (_, { team }) => team,
                deleteTeamSuccess: () => null,
                deleteTeamFailure: () => null,
            },
        ],
    }),
    loaders(({ values, actions }) => ({
        currentTeam: [
            null as TeamType | TeamPublicType | null,
            {
                loadCurrentTeam: async () => {
                    if (!isUserLoggedIn()) {
                        // If user is anonymous (i.e. viewing a shared dashboard logged out), don't load authenticated stuff
                        return null
                    }

                    try {
                        return await api.get('api/environments/@current')
                    } catch {
                        return values.currentTeam
                    }
                },
                updateCurrentTeam: async (payload: Partial<TeamType>, breakpoint) => {
                    if (!values.currentTeam) {
                        throw new Error('Current team has not been loaded yet, so it cannot be updated!')
                    }

                    // session replay config is nested, so we need to make sure we don't overwrite config
                    if (payload.session_replay_config) {
                        payload.session_replay_config = {
                            ...values.currentTeam.session_replay_config,
                            ...payload.session_replay_config,
                        }
                    }

                    let patchedTeam: TeamType | TeamPublicType
                    if (Object.keys(payload).length === 1 && payload.name && values.currentTeam.project_id) {
                        // Renames go through /api/projects, which mirrors the name onto the passthrough
                        // team server-side. /api/environments is deprecated, don't add calls to it
                        const patchedProject = await api.update<ProjectType>(
                            `api/projects/${values.currentTeam.project_id}`,
                            { name: payload.name }
                        )
                        breakpoint()
                        // Sync projectLogic too, as surfaces like the project switcher read currentProject.name
                        actions.loadCurrentProjectSuccess(patchedProject)
                        patchedTeam = { ...values.currentTeam, name: patchedProject.name }
                    } else {
                        patchedTeam = await api.update(`api/environments/${values.currentTeam.id}`, payload)
                        breakpoint()
                    }

                    // We need to reload current org (which lists its teams) in organizationLogic
                    actions.loadCurrentOrganization()

                    /* Notify user the update was successful  */
                    const updatedAttribute =
                        Object.keys(payload).length === 1 ? (Object.keys(payload)[0] as keyof TeamType) : null

                    let message: string
                    if (updatedAttribute === 'feature_flag_confirmation_enabled') {
                        message = payload.feature_flag_confirmation_enabled
                            ? 'Feature flag confirmation enabled'
                            : 'Feature flag confirmation disabled'
                    } else if (updatedAttribute === 'default_evaluation_contexts_enabled') {
                        message = payload.default_evaluation_contexts_enabled
                            ? 'Default evaluation contexts enabled'
                            : 'Default evaluation contexts disabled'
                    } else if (updatedAttribute === 'require_evaluation_contexts') {
                        message = payload.require_evaluation_contexts
                            ? 'Require evaluation contexts enabled'
                            : 'Require evaluation contexts disabled'
                    } else if (
                        updatedAttribute === 'completed_snippet_onboarding' ||
                        updatedAttribute === 'has_completed_onboarding_for'
                    ) {
                        message = "Congrats! You're now ready to use PostHog."
                    } else {
                        message = `${parseUpdatedAttributeName(updatedAttribute)} updated successfully!`
                    }

                    Object.keys(payload).map((property) => {
                        eventUsageLogic
                            .findMounted()
                            ?.actions?.reportTeamSettingChange(property, payload[property as keyof TeamType])
                    })

                    const isUpdatingOnboardingTasks = Object.keys(payload).every((key) => key === 'onboarding_tasks')

                    if (!window.location.pathname.match(/\/(onboarding|products)/) && !isUpdatingOnboardingTasks) {
                        lemonToast.success(message)
                    }

                    const setupLogic = globalSetupLogic.findMounted()
                    if (setupLogic) {
                        if (payload.autocapture_web_vitals_opt_in) {
                            setupLogic.actions.markTaskAsCompleted(SetupTaskId.SetUpWebVitals)
                        }
                        if (payload.session_recording_opt_in) {
                            setupLogic.actions.markTaskAsCompleted(SetupTaskId.SetupSessionRecordings)
                        }
                        if (payload.capture_console_log_opt_in) {
                            setupLogic.actions.markTaskAsCompleted(SetupTaskId.EnableConsoleLogs)
                        }
                        if (
                            payload.session_recording_sample_rate ||
                            payload.session_recording_minimum_duration_milliseconds ||
                            payload.session_recording_linked_flag ||
                            payload.session_recording_network_payload_capture_config
                        ) {
                            setupLogic.actions.markTaskAsCompleted(SetupTaskId.ConfigureRecordingSettings)
                        }
                    }

                    return patchedTeam
                },
                createTeam: async ({ name, is_demo }: { name: string; is_demo: boolean }) => {
                    if (!values.currentProject) {
                        throw new Error(
                            'Environment could not be created, because the parent project has not been loaded yet!'
                        )
                    }
                    return await api.create(`api/projects/${values.currentProject.id}/environments/`, { name, is_demo })
                },
                // Project API Token
                resetToken: async () => await api.update(`api/environments/${values.currentTeamId}/reset_token`, {}),
                // Feature Flags Secure API Token
                rotateSecretToken: async () =>
                    await api.update(`api/environments/${values.currentTeamId}/rotate_secret_token`, {}),
                deleteSecretTokenBackup: async () =>
                    await api.update(`api/environments/${values.currentTeamId}/delete_secret_token_backup`, {}),
                /**
                 * If adding a product intent that also represents regular product usage, see explainer in posthog.models.product_intent.product_intent.py.
                 * Also, we refresh the list of custom products to show the possible new entry in the sidebar after we've added the intent.
                 */
                addProductIntent: async (properties: ProductIntentProperties) => {
                    const result = await addProductIntent(properties)
                    actions.loadCustomProducts()

                    return withProductIntentsFrom(values.currentTeam, result)
                },
                addProductIntentForCrossSell: async (properties: ProductCrossSellProperties) => {
                    const result = await addProductIntentForCrossSell(properties)
                    actions.loadCustomProducts()

                    return withProductIntentsFrom(values.currentTeam, result)
                },
                recordProductIntentOnboardingComplete: async (properties: ProductOnboardingCompleteProperties) => {
                    const result = await completeProductOnboarding(values.currentTeamIdStrict, properties)
                    actions.loadCustomProducts()

                    return withProductIntentsFrom(values.currentTeam, result)
                },
            },
        ],
    })),
    selectors(() => ({
        hasOnboardedAnyProduct: [
            (selectors) => [selectors.currentTeam],
            (currentTeam: TeamPublicType | TeamType | null): boolean => {
                if (
                    currentTeam &&
                    !currentTeam.completed_snippet_onboarding &&
                    !Object.keys(currentTeam.has_completed_onboarding_for || {}).length
                ) {
                    return false
                }
                return true
            },
        ],
        currentTeamId: [
            (selectors) => [selectors.currentTeam],
            (currentTeam: TeamPublicType | TeamType | null): number | null => (currentTeam ? currentTeam.id : null),
        ],
        // currentTeamIdStrict is currentTeamId with throwing an error to be consistent with the
        // newly introduced currentProjectId and currentOrganizationId. To not cause a breaking change
        // we kept currentTeamId as is for now. Ultimately throwing an error should be migrated to
        // currentTeamId and so that currentTeamIdStrict can be removed.
        currentTeamIdStrict: [
            (selectors) => [selectors.currentTeam],
            (currentTeam: TeamPublicType | TeamType | null): number | string => {
                if (!currentTeam || !currentTeam.id) {
                    // TODO: Fix callers that access currentTeamIdStrict before the team is loaded,
                    // then restore the throw. Temporarily falling back to "@current" to avoid crashes.
                    posthog.captureException(new Error('currentTeamId accessed before team loaded'), {
                        severity: 'warning',
                        tag: 'selector_accessed_before_loaded',
                    })
                    return '@current'
                }
                return currentTeam.id
            },
        ],
        currentProjectId: [
            (selectors) => [selectors.currentTeam],
            (currentTeam: TeamPublicType | TeamType | null): number | string => {
                if (!currentTeam || !currentTeam.project_id) {
                    // TODO: Fix callers that access currentProjectId before the team is loaded,
                    // then restore the throw. Temporarily falling back to "@current" to avoid crashes.
                    posthog.captureException(new Error('currentProjectId accessed before team loaded'), {
                        severity: 'warning',
                        tag: 'selector_accessed_before_loaded',
                    })
                    return '@current'
                }
                return currentTeam.project_id
            },
        ],
        isCurrentTeamUnavailable: [
            (selectors) => [selectors.currentTeam, selectors.currentTeamLoading],
            // If project has been loaded and is still null, it means the user just doesn't have access.
            (currentTeam: TeamPublicType | TeamType | null, currentTeamLoading: boolean): boolean =>
                (!currentTeam?.effective_membership_level || currentTeam.user_access_level === 'none') &&
                !currentTeamLoading,
        ],
        demoOnlyProject: [
            (selectors) => [selectors.currentTeam, organizationLogic.selectors.currentOrganization],
            (
                currentTeam: TeamPublicType | TeamType | null,
                currentOrganization: null | import('~/types').OrganizationType
            ): boolean =>
                (currentTeam?.is_demo && currentOrganization?.teams && currentOrganization.teams.length == 1) || false,
        ],
        funnelCorrelationConfig: [
            (selectors) => [selectors.currentTeam],
            (currentTeam: TeamPublicType | TeamType | null): CorrelationConfigType => {
                return currentTeam?.correlation_config || {}
            },
        ],
        timezone: [
            (selectors) => [selectors.currentTeam],
            (currentTeam: TeamPublicType | TeamType | null): string => currentTeam?.timezone || 'UTC',
        ],
        /** 0 means Sunday, 1 means Monday. */
        weekStartDay: [
            (selectors) => [selectors.currentTeam],
            (currentTeam: TeamPublicType | TeamType | null): number => currentTeam?.week_start_day || 0,
        ],
        isTeamTokenResetAvailable: [
            (selectors) => [selectors.currentTeam],
            (currentTeam: TeamPublicType | TeamType | null): boolean =>
                (!!currentTeam?.effective_membership_level &&
                    currentTeam.effective_membership_level >= OrganizationMembershipLevel.Admin) ||
                currentTeam?.user_access_level === 'admin',
        ],
        testAccountFilterFrequentMistakes: [
            (selectors) => [selectors.currentTeam],
            (currentTeam: TeamPublicType | TeamType | null): FrequentMistakeAdvice[] => {
                if (!currentTeam) {
                    return []
                }
                const frequentMistakes: FrequentMistakeAdvice[] = []

                for (const filter of currentTeam.test_account_filters || []) {
                    if (filter.key === 'email' && filter.type === 'event') {
                        frequentMistakes.push({
                            key: 'email',
                            type: 'event',
                            fix: 'it is more common to filter email by person properties, not event properties',
                        })
                    }
                }
                return frequentMistakes
            },
        ],
        baseCurrency: [
            (selectors) => [selectors.currentTeam],
            (currentTeam: TeamType): CurrencyCode => currentTeam?.base_currency ?? DEFAULT_CURRENCY,
        ],
        customerAnalyticsConfig: [
            (s) => [s.currentTeam],
            (currentTeam: TeamType): CustomerAnalyticsConfig =>
                currentTeam?.customer_analytics_config ?? ({} as CustomerAnalyticsConfig),
        ],
    })),
    listeners(({ actions }) => ({
        loadCurrentTeamSuccess: ({ currentTeam }) => {
            if (currentTeam) {
                ApiConfig.setCurrentTeamId(currentTeam.id)
            }
        },
        updateCurrentTeamSuccess: () => {
            // Reload user after team update to keep user object in sync
            actions.loadUser()
        },
        createTeamSuccess: ({ currentTeam }) => {
            if (currentTeam) {
                actions.switchTeam(currentTeam.id)
            }
        },
        deleteTeam: async ({ team }) => {
            try {
                await api.delete(`api/environments/${team.id}`)
                location.reload()
                actions.deleteTeamSuccess()
            } catch {
                actions.deleteTeamFailure()
            }
        },
        deleteTeamSuccess: () => {
            lemonToast.success('Project has been deleted')
        },
    })),
    afterMount(({ actions }) => {
        const appContext = getAppContext()
        const currentTeam = appContext?.current_team
        const switchedTeam = appContext?.switched_team
        if (switchedTeam) {
            lemonToast.info(<>You've switched to&nbsp;project {currentTeam?.name}</>, {
                button: {
                    label: 'Switch back',
                    action: () => actions.switchTeam(switchedTeam),
                },
                icon: <IconSwapHoriz />,
            })
        }

        if (currentTeam) {
            // If app context is available (it should be practically always) we can immediately know currentTeam
            actions.loadCurrentTeamSuccess(currentTeam)
        } else {
            // If app context is not available, a traditional request is needed
            actions.loadCurrentTeam()
        }
    }),
])
